import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { GROQButton } from '@/sanity/lib/definitions';
import { stegaClean } from '@sanity/client/stega';
import { Slot } from '@radix-ui/react-slot';
import { CTAWrapper } from '@/components/global/utils/CTAWrapper';
import { iconMap } from '@/lib/iconMap';

const buttonVariants = cva('button cta', {
  variants: {
    variant: {
      default: 'button-green',
      purple: 'button-purple',
      outline: 'button-outline',
      outlineBlur: 'button-outline-blur',
      lightGreen: 'button-light-green',
      lightYellow: 'button-light-yellow',
      white: 'button-white',
      yellow: 'button-yellow',
      darkGlow: 'button-dark-glow',
      lightGlow: 'button-light-glow',
    },
    action: {
      link: '',
      popup: '',
      anchor: '',
      none: '',
    },
  },
  defaultVariants: {
    variant: 'default',
    action: 'none',
  },
});

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, action, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const children = props.children;
    return (
      <Comp
        className={cn('group', buttonVariants({ variant, className }))}
        data-type={variant}
        data-action={action}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export interface CTAButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  data: GROQButton;
  headerButton?: boolean;
}

const CTAButton = React.forwardRef<HTMLButtonElement, CTAButtonProps>(
  ({ className, data, headerButton = false, ...props }, ref) => {
    const cleanedData = stegaClean(data ?? {}) ?? null;

    const type = cleanedData?.type ?? 'default';

    const hasIcon = cleanedData?.cta?.icon ?? 'none';
    const icon = iconMap[hasIcon as keyof typeof iconMap];

    const action = cleanedData?.cta?.action ?? null;

    const currentYear = new Date().getFullYear().toString();
    const firstDigits = currentYear.slice(0, 2);
    const lastDigits = currentYear.slice(2, 4);

    const isDarkGlow = type === 'darkGlow';
    const isLightGlow = type === 'lightGlow';
    const isGlow = isDarkGlow || isLightGlow;
    const label = data?.cta
      ? (data.cta.title ?? 'Please add a title')
      : 'Please select a CTA';

    const buttonElement = (
      <Button
        className="flex relative justify-center items-center w-full group gap-[1.25rem] overflow-clip"
        variant={type}
        action={action ?? 'none'}
        ref={ref}
        {...props}
      >
        {isGlow ? (
          <div className="absolute top-0 left-0 w-full h-full button-bg" />
        ) : (
          <>
            <div className="absolute top-0 left-0 w-full h-full transition-transform duration-500 group-hover:-translate-y-full button-bg" />
            <div className="absolute top-0 left-0 w-full h-full transition-transform duration-500 translate-y-full group-hover:translate-y-0 button-bg-hover" />
          </>
        )}

        <div className="relative leading-none overflow-clip">
          <div className="transition-transform duration-500 group-hover:-translate-y-full button-text">
            {label}
          </div>
          <div className="absolute top-0 left-0 transition-transform duration-500 translate-y-full group-hover:translate-y-0 button-text-hover">
            {label}
          </div>
        </div>

        {isGlow && (
          <div className="relative z-10 h-[2.5rem] w-[2.5rem] shrink-0 overflow-clip">
            <div className="transition-transform duration-500 group-hover:translate-x-full">
              <svg
                className="h-[2.5rem] w-[2.5rem]"
                viewBox="0 0 15 12.5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <title>Arrow right</title>
                <path
                  d="M8.75 0L7.85625 0.870625L12.5937 5.625H0V6.875H12.5937L7.85625 11.6081L8.75 12.5L15 6.25L8.75 0Z"
                  fill={isLightGlow ? '#0a2540' : '#e2f2f3'}
                />
              </svg>
            </div>
            <div className="absolute top-0 left-0 transition-transform duration-500 -translate-x-full group-hover:delay-200 group-hover:translate-x-0">
              <svg
                className="h-[2.5rem] w-[2.5rem]"
                viewBox="0 0 15 12.5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <title>Arrow right</title>
                <path
                  d="M8.75 0L7.85625 0.870625L12.5937 5.625H0V6.875H12.5937L7.85625 11.6081L8.75 12.5L15 6.25L8.75 0Z"
                  fill={isLightGlow ? '#0a2540' : '#e2f2f3'}
                />
              </svg>
            </div>
          </div>
        )}
      </Button>
    );

    return (
      <CTAWrapper
        cta={cleanedData?.cta}
        className={cn(
          'group inline-block relative z-10 w-full sm:w-auto ',
          headerButton ? 'min-w-[11.375rem]' : 'min-w-[25.75rem]',
          className,
        )}
      >
        {buttonElement}
      </CTAWrapper>
    );
  },
);
CTAButton.displayName = 'CTAButton';

export { Button, buttonVariants, CTAButton };
